/**
 * Shared geometry for the search map's "draw an area" tools.
 *
 * The drawn region (a radius circle or a free-form polygon) is sketched in
 * MapView, but it must also drive the grid/list view and the grid's mini-map
 * preview — so the region shape and the point-in-region test live here, in one
 * dependency-free place, instead of being trapped inside MapView. The polygon
 * test is plain ray-casting (no `google.maps.geometry` dependency) so it works
 * even when the full Maps library has not loaded yet — e.g. in grid view before
 * the map has ever been opened.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export type DrawnRegion =
  | { kind: 'radius'; center: LatLng; radiusMeters: number }
  | { kind: 'polygon'; path: LatLng[] }
  | null;

const EARTH_RADIUS_M = 6371000;

/** Great-circle distance in metres between two coordinates (Haversine). */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Ray-casting point-in-polygon test on planar lat/lng. Accurate at the
 * city/neighbourhood scale this feature targets, and mirrors the result of the
 * `google.maps.geometry.poly.containsLocation` test used inside the live map.
 */
export function pointInPolygon(point: LatLng, path: LatLng[]): boolean {
  if (path.length < 3) return false;
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i, i += 1) {
    const xi = path[i].lng;
    const yi = path[i].lat;
    const xj = path[j].lng;
    const yj = path[j].lat;
    const intersects =
      yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** True if a coordinate falls inside the drawn region (a null region matches everything). */
export function isInsideRegion(point: LatLng, region: DrawnRegion): boolean {
  if (!region) return true;
  if (region.kind === 'radius') {
    return haversineMeters(region.center, point) <= region.radiusMeters;
  }
  return pointInPolygon(point, region.path);
}

/**
 * Lat/lng points that frame the region when fed to a `LatLngBounds.extend`
 * loop. For a polygon these are its vertices; for a radius circle they are the
 * two corners of its bounding box, derived from the radius alone so callers
 * don't need `Circle.getBounds` (absent from the app's slim Maps typings).
 */
export function regionBoundsPoints(region: DrawnRegion): LatLng[] {
  if (!region) return [];
  if (region.kind === 'polygon') return region.path;
  const { center, radiusMeters } = region;
  const dLat = radiusMeters / 111320; // metres per degree of latitude
  const cos = Math.cos((center.lat * Math.PI) / 180);
  const dLng = radiusMeters / (111320 * (Math.abs(cos) < 1e-6 ? 1e-6 : cos));
  return [
    { lat: center.lat + dLat, lng: center.lng + dLng },
    { lat: center.lat - dLat, lng: center.lng - dLng },
  ];
}

interface GeoItem {
  latitude: number;
  longitude: number;
}

/**
 * Keep only the items whose coordinates fall inside the drawn region. With no
 * region the input array is returned untouched (same reference) so callers can
 * use it unconditionally without paying for a copy or breaking memo identity.
 */
export function filterByRegion<T extends GeoItem>(items: T[], region: DrawnRegion): T[] {
  if (!region) return items;
  return items.filter(
    (item) =>
      item.latitude != null &&
      item.longitude != null &&
      isInsideRegion({ lat: item.latitude, lng: item.longitude }, region),
  );
}

// --- boundary → region helpers (city/district search previews) --------------

export interface GeoGeometry {
  type: 'Polygon' | 'MultiPolygon' | string;
  coordinates: number[][][] | number[][][][];
}

/** Outer rings ([lng,lat][]) of a GeoJSON Polygon/MultiPolygon. */
function outerRings(geometry: GeoGeometry | null | undefined): number[][][] {
  if (!geometry || !geometry.coordinates) return [];
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as number[][][][]).map((poly) => poly?.[0]).filter(Boolean);
  }
  const first = (geometry.coordinates as number[][][])[0];
  return first ? [first] : [];
}

/**
 * GeoJSON polygon → a DrawnRegion polygon (largest outer ring). Used to frame +
 * outline a searched DISTRICT on the mini-map. Null when there's no usable ring.
 */
export function geometryToRegion(geometry: GeoGeometry | null | undefined): DrawnRegion {
  const rings = outerRings(geometry);
  if (rings.length === 0) return null;
  const ring = rings.reduce((a, b) => (b.length > a.length ? b : a));
  const path = ring
    .filter((c) => Array.isArray(c) && c.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));
  return path.length >= 3 ? { kind: 'polygon', path } : null;
}

/**
 * Bounding-box corner points across many geometries — for framing a whole CITY
 * on the mini-map (no outline drawn). Returns [] when there's nothing to frame.
 */
export function boundsCorners(geometries: (GeoGeometry | null | undefined)[]): LatLng[] {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const g of geometries) {
    for (const ring of outerRings(g)) {
      for (const c of ring) {
        if (!Array.isArray(c) || c.length < 2) continue;
        const [lng, lat] = c;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      }
    }
  }
  if (!Number.isFinite(minLat)) return [];
  return [{ lat: maxLat, lng: maxLng }, { lat: minLat, lng: minLng }];
}

/** All polygon vertices across many geometries, as LatLng[] (for a city hull). */
export function allVertices(geometries: (GeoGeometry | null | undefined)[]): LatLng[] {
  const out: LatLng[] = [];
  for (const g of geometries) {
    for (const ring of outerRings(g)) {
      for (const c of ring) {
        if (Array.isArray(c) && c.length >= 2) out.push({ lat: c[1], lng: c[0] });
      }
    }
  }
  return out;
}

/**
 * Convex hull (Andrew's monotone chain) of lat/lng points → an outer outline.
 * Used to draw a single clean border around a searched CITY's districts. Returns
 * the input (or []) when there are fewer than 3 usable points.
 */
export function convexHull(points: LatLng[]): LatLng[] {
  const pts = points
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .slice()
    .sort((a, b) => a.lng - b.lng || a.lat - b.lat);
  if (pts.length < 3) return pts;
  const cross = (o: LatLng, a: LatLng, b: LatLng) =>
    (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
  const lower: LatLng[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: LatLng[] = [];
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}
